var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createRef, Fragment, useCallback, useEffect, useState, } from 'react';
import { flip, offset, shift, useFloating, } from '@floating-ui/react-dom';
import { Portal, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { useDocument } from '../hooks/useDocument.js';
import { FloatingFullContext, useFloatingFullContext, } from '../utils/floating/index.js';
import { TooltipContext, useTooltip } from './TooltipContext.js';
export const Tooltip = ({ children, placement = 'top', strategy = 'absolute', }) => {
    const { document } = useDocument();
    const [visible, setVisible] = useState(false);
    const { x, y, reference, floating, refs, update } = useFloating({
        placement,
        strategy,
        middleware: [offset(4), shift(), flip()],
    });
    const showTooltip = useCallback(() => {
        setVisible(true);
    }, [setVisible]);
    const hideTooltip = useCallback(() => {
        setVisible(false);
    }, [setVisible]);
    useEffect(() => {
        const handleClickOutside = event => {
            var _a, _b, _c, _d;
            const target = ((_b = (_a = event.composedPath) === null || _a === void 0 ? void 0 : _a.call(event)) === null || _b === void 0 ? void 0 : _b[0]) || event.target;
            if (target instanceof Node) {
                if (refs.floating.current != null &&
                    refs.reference.current != null &&
                    !((_c = refs.floating.current) === null || _c === void 0 ? void 0 : _c.contains(target)) &&
                    !((_d = refs.reference.current) === null || _d === void 0 ? void 0 : _d.contains(target))) {
                    hideTooltip();
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [refs.reference, refs.floating.current, hideTooltip]);
    useEffect(() => {
        var _a, _b;
        if (refs.reference.current == null) {
            return;
        }
        (_a = refs.reference.current) === null || _a === void 0 ? void 0 : _a.addEventListener('mouseenter', showTooltip);
        (_b = refs.reference.current) === null || _b === void 0 ? void 0 : _b.addEventListener('mouseleave', hideTooltip);
        return () => {
            var _a, _b;
            (_a = refs.reference.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('mouseenter', showTooltip);
            (_b = refs.reference.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('mouseleave', hideTooltip);
        };
    }, [refs.reference, showTooltip, hideTooltip]);
    return (React.createElement(TooltipContext.Provider, { value: {
            open: visible,
            onOpen: showTooltip,
            onClose: hideTooltip,
        } },
        React.createElement(FloatingFullContext.Provider, { value: {
                x,
                y,
                strategy,
                reference,
                floating,
                update,
            } }, children)));
};
const TooltipTrigger = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    const { reference } = useFloatingFullContext();
    return (React.createElement("div", Object.assign({ ref: reference }, props), children));
};
const TooltipPanel = ({ children, className, inverted = true, }) => {
    const { open } = useTooltip();
    const { x, y, strategy, floating } = useFloatingFullContext();
    const panelRef = createRef();
    const arrowRef = createRef();
    const style = {
        position: strategy,
        top: '0',
        left: '0',
        transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
    };
    if (!children) {
        return null;
    }
    return (React.createElement(Portal, null,
        React.createElement("div", { ref: panelRef, style: style, className: className },
            React.createElement(Transition, { show: open, as: Fragment, enter: "transition ease-out duration-200", enterFrom: "opacity-0 translate-y-1", enterTo: "opacity-100 translate-y-0", leave: "transition ease-in duration-150", leaveFrom: "opacity-100 translate-y-0", leaveTo: "opacity-0 translate-y-1", beforeEnter: () => floating(panelRef.current), afterLeave: () => floating(null) },
                React.createElement("div", { className: clsx('py-2 px-3', 'w-max max-w-[calc(100vw-20px)] sm:max-w-md rounded-lg', 'text-center text-xs pointer-events-none', inverted
                        ? 'bg-surface-inverse text-content-on-inverse'
                        : 'bg-surface text-content-subdued border border-line-subdued rounded-md shadow-lg') },
                    children,
                    React.createElement("div", { ref: arrowRef }))))));
};
Tooltip.Trigger = TooltipTrigger;
Tooltip.Panel = TooltipPanel;
//# sourceMappingURL=Tooltip.js.map